// Inline sanitizeNoWhitespace to avoid backend dependency
function sanitizeNoWhitespace(s, maxLength) {
    return s.replace(/\s+/g, '').slice(0, maxLength);
}
const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const Weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const WeekdaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const Months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MonthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MIDNIGHT = { hour: 0, minute: 0 };
export const BAD_DATE = { year: 0, month: 0, date: 0 };
export const BAD_TIME = { hour: 0, minute: 0 };
export const BAD_DATE_TIME = { year: 0, month: 0, date: 0, hour: 0, minute: 0 };
export function simpleDate(year, month, date = 1, hour = 0, minute = 0) {
    return { year, month, date, hour, minute };
}
export function simpleTime(hour, minute = 0) {
    return { hour, minute };
}
export function simpleTimeOnly(t) {
    return { hour: (t === null || t === void 0 ? void 0 : t.hour) || 0, minute: (t === null || t === void 0 ? void 0 : t.minute) || 0 };
}
export function simpleDateOnly(d) {
    return { year: (d === null || d === void 0 ? void 0 : d.year) || 0, month: (d === null || d === void 0 ? void 0 : d.month) || 0, date: (d === null || d === void 0 ? void 0 : d.date) || 0 };
}
export function isLeapYear(year) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}
export function getDaysInYear(year) {
    return isLeapYear(year) ? 366 : 365;
}
export function getMonthDays(month, year) {
    if (month < 1 || month > 12)
        throw new Error('Invalid month');
    if (month == 2 && isLeapYear(year))
        return 29;
    return DAYS_PER_MONTH[month - 1];
}
export function fixSimpleTime(t) {
    if (t.hour >= 0 && t.hour < 24 && t.minute >= 0 && t.minute < 60) {
        return t;
    }
    // Handle negative minutes properly
    let totalMinutes = t.minute;
    let hourAdjustment = 0;
    if (totalMinutes < 0) {
        hourAdjustment = Math.floor(totalMinutes / 60);
        totalMinutes = totalMinutes - (hourAdjustment * 60);
    }
    else {
        hourAdjustment = Math.floor(totalMinutes / 60);
        totalMinutes = totalMinutes % 60;
    }
    const totalHours = t.hour + hourAdjustment;
    const dayOverflow = Math.floor(totalHours / 24);
    const correctedHour = ((totalHours % 24) + 24) % 24;
    const result = {
        hour: correctedHour,
        minute: totalMinutes
    };
    if (dayOverflow !== 0) {
        result.dayOverflow = dayOverflow;
    }
    return result;
}
export function fixSimpleDate(t) {
    let year = t.year;
    let month = t.month;
    let date = t.date;
    if (year >= 1 && year <= 2500 && month >= 1 && month <= 12 && date >= 1 && date <= getMonthDays(month, year))
        return t;
    // Fix year first (minimum year is 1), and use sanity for the rest
    year = Math.max(1, Math.min(year, 2500));
    month = Math.max(-10000, Math.min(month, 10000));
    // Don't clamp date values - let the year loop handle large values properly
    // date = Math.max(-10000, Math.min(date, 10000));
    // Fix month overflow/underflow
    while (month > 12) {
        month -= 12;
        year += 1;
    }
    while (month < 1) {
        month += 12;
        year -= 1;
        if (year < 1) {
            year = 1;
            month = 1;
            break;
        }
    }
    // Optimize for large day values - handle years first if |days| >= 365
    if (date >= 365) {
        // For large positive day values, add full years first
        while (year < 2500) {
            const daysInYear = getDaysInYear(year);
            if (date >= daysInYear) {
                date -= daysInYear;
                year += 1;
            }
            else {
                break;
            }
        }
        // If we hit the year limit, clamp to max year and set to last day
        if (year >= 2500) {
            year = 2500;
            month = 12;
            date = 31;
            return { year, month, date };
        }
    }
    else if (date <= -365) {
        // For large negative day values, subtract full years first
        while (date <= -365) {
            year -= 1;
            if (year < 1) {
                year = 1;
                month = 1;
                date = 1;
                return { year, month, date };
            }
            const daysInPrevYear = getDaysInYear(year);
            if (date <= -daysInPrevYear) {
                date += daysInPrevYear;
            }
            else {
                break;
            }
        }
    }
    // Fix remaining date overflow/underflow with month-by-month approach
    while (date < 1) {
        month -= 1;
        if (month < 1) {
            month = 12;
            year -= 1;
            if (year < 1) {
                year = 1;
                month = 1;
                date = 1;
                break;
            }
        }
        date += getMonthDays(month, year);
    }
    while (true) {
        const daysInMonth = getMonthDays(month, year);
        if (date <= daysInMonth)
            break;
        date -= daysInMonth;
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }
    return { year, month, date };
}
export function fixSimpleDateTime(dateTime) {
    const t = fixSimpleTime(dateTime);
    if (t.dayOverflow) {
        const d = fixSimpleDate({ year: dateTime.year, month: dateTime.month, date: dateTime.date + t.dayOverflow });
        return { ...t, year: d.year, month: d.month, date: d.date };
    }
    const d = fixSimpleDate(dateTime);
    if (d === dateTime)
        return dateTime;
    return { ...d, hour: t.hour, minute: t.minute };
}
export function dateNow(timeZone) {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false
    }).formatToParts(now).reduce((acc, part) => {
        if (part.type !== 'literal') {
            acc[part.type] = parseInt(part.value);
        }
        return acc;
    }, {});
    return {
        year: parts.year,
        month: parts.month,
        date: parts.day,
        hour: parts.hour == 24 ? 0 : parts.hour,
        minute: parts.minute
    };
}
export function valiDate(date) {
    if (date.year < 1900 || date.year > 2100 || date.month < 1 || date.month > 12 || date.date < 1 || date.date > 31 || date.date > getMonthDays(date.month, date.year))
        return false;
    if ('hour' in date) {
        if (date.hour < 0 || date.hour > 23 || date.minute < 0 || date.minute > 59)
            return false;
    }
    return true;
}
export function parseYMD(s) {
    const match = sanitizeNoWhitespace(s, 12).match(/^(\d{4})(\d{2})(\d{2})(\d{4})?$/);
    if (!match)
        return undefined;
    const year = parseInt(match[1]);
    const month = parseInt(match[2]);
    const date = parseInt(match[3]);
    const d = { year, month, date };
    if (!valiDate(d)) {
        return undefined;
    }
    return d;
}
export function formatYMD(date) {
    return `${date.year}${date.month.toString().padStart(2, '0')}${date.date.toString().padStart(2, '0')}`;
}
export function formatHHMM(time) {
    return `${time.hour.toString().padStart(2, '0')}${time.minute.toString().padStart(2, '0')}`;
}
export function formatHHMM12(time) {
    const hour = time.hour % 12 || 12;
    return `${hour.toString().padStart(2, '0')}${time.minute.toString().padStart(2, '0')} ${time.hour < 12 ? 'AM' : 'PM'}`;
}
export function parseYMDHM(s, defaultValue) {
    if (!s)
        return defaultValue;
    const match = sanitizeNoWhitespace(s, 12).match(/^(\d{4})(\d{2})(\d{2})(?:(\d{2})(\d{2}))?$/);
    if (!match)
        return defaultValue;
    const year = parseInt(match[1]);
    const month = parseInt(match[2]);
    const date = parseInt(match[3]);
    const hour = match[4] ? parseInt(match[4]) : 0;
    const minute = match[5] ? parseInt(match[5]) : 0;
    return fixSimpleDateTime({ year, month, date, hour, minute });
}
export function formatYMDHM(dateTime) {
    return `${formatYMD(dateTime)}${(dateTime.hour || 0).toString().padStart(2, '0')}${(dateTime.minute || 0).toString().padStart(2, '0')}`;
}
export function parseHHMM(t) {
    const s = t.length == 12 ? t.slice(8, 12) : t;
    if (!/^\d{4}$/.test(s))
        return undefined;
    const h = parseInt(s.slice(0, 2));
    const m = parseInt(s.slice(2, 4));
    if (isNaN(h) || isNaN(m))
        return undefined;
    return {
        hour: h,
        minute: m
    };
}
export function dateEqual(a, b) {
    var _a, _b, _c, _d;
    if (a == b)
        return true;
    if (!a || !b)
        return false;
    if (a.year != b.year || a.month != b.month || a.date != b.date)
        return false;
    if ('hour' in a || 'hour' in b)
        return ((_a = a.hour) !== null && _a !== void 0 ? _a : 0) == ((_b = b.hour) !== null && _b !== void 0 ? _b : 0) && ((_c = a.minute) !== null && _c !== void 0 ? _c : 0) == ((_d = b.minute) !== null && _d !== void 0 ? _d : 0);
    return true;
}
export function dateLess(a, b) {
    if (a.year != b.year)
        return a.year < b.year;
    if (a.month != b.month)
        return a.month < b.month;
    if (a.date != b.date || (a.hour == null && b.hour == null))
        return a.date < b.date;
    if (a.hour == null && b.hour != null)
        return b.hour == 0 && b.minute == 0;
    if (a.hour != null && b.hour == null)
        return false;
    if (a.hour != b.hour)
        return a.hour < b.hour;
    return a.minute < b.minute;
}
export function dateGreater(a, b) {
    if (a.year != b.year)
        return a.year > b.year;
    if (a.month != b.month)
        return a.month > b.month;
    if (a.date != b.date || (a.hour == null && b.hour == null))
        return a.date > b.date;
    if (a.hour != null && b.hour == null)
        return a.hour == 0 && a.minute == 0;
    if (a.hour == null && b.hour != null)
        return false;
    if (a.hour != b.hour)
        return a.hour > b.hour;
    return a.minute > b.minute;
}
export function dateGEQ(a, b) {
    return !dateLess(a, b);
}
export function dateLEQ(a, b) {
    return !dateGreater(a, b);
}
export function dateCompare(a, b) {
    if (dateEqual(a, b))
        return 0;
    if (dateLess(a, b))
        return -1;
    return 1;
}
export function dateMin(date1, ...dates) {
    let min = date1;
    for (let i = 0; i < dates.length; i++)
        if (dateLess(dates[i], min))
            min = dates[i];
    return min;
}
export function dateMax(date1, ...dates) {
    let max = date1;
    for (let i = 0; i < dates.length; i++)
        if (dateLess(max, dates[i]))
            max = dates[i];
    return max;
}
export function getWeekStart(date) {
    const dayOfWeek = getWeekDay(date); // 0 = Sunday, 1 = Monday, ...
    const mondayOffset = dayOfWeek == 0 ? -6 : 1 - dayOfWeek; // Get Monday
    return addDays(date, mondayOffset);
}
export function getMonthStart(date) {
    return { year: date.year, month: date.month, date: 1 };
}
export function getWeekDay(date) {
    if (date._weekDay != null)
        return date._weekDay;
    let { year, month, date: day } = date;
    // Zeller's congruence formula
    if (month < 3) {
        month += 12;
        year -= 1;
    }
    const century = Math.floor(year / 100);
    const yearOfCentury = year % 100;
    const h = (day +
        Math.floor((13 * (month + 1)) / 5) +
        yearOfCentury +
        Math.floor(yearOfCentury / 4) +
        Math.floor(century / 4) -
        2 * century) % 7;
    // Convert Zeller's result (0=Saturday, 1=Sunday, ..., 6=Friday) to (0=Sunday, 1=Monday, ..., 6=Saturday)
    const weekDay = (h + 6) % 7;
    date._weekDay = weekDay; // cache it
    return weekDay;
}
export function addDays(date, days) {
    const newWeekDay = date._weekDay != null ? ((date._weekDay + days) % 7 + 7) % 7 : undefined;
    if (date.hour == null)
        return fixSimpleDate({ ...date, date: date.date + days, _weekDay: newWeekDay });
    else
        return fixSimpleDateTime({ ...date, date: date.date + days, _weekDay: newWeekDay });
}
export function addMonths(date, months) {
    if (date.hour == null)
        return fixSimpleDate({ ...date, month: date.month + months, _weekDay: undefined });
    else
        return fixSimpleDateTime({ ...date, month: date.month + months, _weekDay: undefined });
}
export function addYears(date, years) {
    if (date.hour == null)
        return fixSimpleDate({ ...date, year: date.year + years, _weekDay: undefined });
    else
        return fixSimpleDateTime({ ...date, year: date.year + years, _weekDay: undefined });
}
/**
 * Adds a specified duration to a date based on the unit provided.
 * @param amount - The amount of time to add (can be negative to subtract)
 * @param unit - The unit of time to add (minutes, hours, days, or endofday)
 * @returns A new date with the duration added
 */
export function addDuration(date, amount, unit) {
    let d = date;
    switch (unit) {
        case 'minutes':
            d = { ...date, minute: date.minute + (amount || 0) };
            break;
        case 'hours':
            d = { ...date, hour: date.hour + (amount || 0) };
            break;
        case 'days':
            d = { ...date, date: date.date + (amount || 0) };
            break;
        case 'endofday':
            // Set to end of day (23:59)
            d = { ...date, hour: 23, minute: 59 };
            break;
        case 'none':
            // No due date - return the same date (no change)
            break;
        default:
            throw new Error(`Unsupported duration unit: ${unit}`);
    }
    return fixSimpleDateTime(d);
}
export function dateDiffInDays(a, b) {
    if (dateGreater(a, b))
        return -dateDiffInDays(b, a);
    if (b.year - 30 > a.year) // bigger 30 years? optimize here, details don't matter, won't be accurate
        return dateDiffInDays({ year: b.year - 20, month: a.month, date: a.date }, b) + Math.round((b.year - a.year - 20) * 365.25);
    let d = 0;
    if (a.year == b.year && a.month == b.month)
        return b.date - a.date;
    // Count days remaining in the first month
    d += getMonthDays(a.month, a.year) - a.date;
    d += b.date;
    let m = a.month == 12 ? 1 : a.month + 1;
    let y = a.month == 12 ? a.year + 1 : a.year;
    while (y < b.year || m < b.month) {
        d += getMonthDays(m, y);
        m += 1;
        if (m > 12) {
            m = 1;
            y += 1;
        }
    }
    return d;
}
export function dateDiffInMonths(a, b) {
    return (b.year - a.year) * 12 + (b.month - a.month);
}
export function dateDiffInYears(a, b) {
    return b.year - a.year;
}
// calculates diff in hours. Ignores daytime saving changes and other irregularities.
export function dateDiffInHours(a, b) {
    const hourDiff = (b.hour - a.hour) + Math.floor((b.minute - a.minute) / 60);
    if ('year' in a && 'year' in b)
        return dateDiffInDays(a, b) * 24 + hourDiff;
    return hourDiff;
}
// calculates diff in minutes. Ignores daytime saving changes and other irregularities.
export function dateDiffInMinutes(a, b) {
    const minuteDiff = (b.minute - a.minute) + (b.hour - a.hour) * 60;
    if ('year' in a && 'year' in b)
        return dateDiffInDays(a, b) * 24 * 60 + minuteDiff;
    return minuteDiff;
}
export function mergeDate(partial, date) {
    return { year: partial.year || date.year, month: partial.month || date.month, date: partial.date || date.date };
}
export function midnightOf(date) {
    return { ...date, hour: 0, minute: 0 };
}
// JS Date interop functions
function convertJSDateToTimeZone(date, timeZone) {
    // Get the date string in the target timezone
    const tzString = date.toLocaleString("en-US", {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    // Parse the string back to a Date
    return new Date(tzString);
}
export function getJSDateFromSimpleDate(date, timeZone) {
    var _a, _b;
    const dt = date;
    const hour = (_a = dt.hour) !== null && _a !== void 0 ? _a : 0;
    const minute = (_b = dt.minute) !== null && _b !== void 0 ? _b : 0;
    const dateString = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.date).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
    const tempDate = new Date(Date.UTC(date.year, date.month - 1, date.date, hour, minute));
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    const targetDateString = formatter.format(tempDate).replace(/, /g, 'T');
    const intendedTime = new Date(`${dateString}Z`).getTime();
    const actualTime = new Date(targetDateString.replace('24:', '00:') + 'Z').getTime();
    const offset = intendedTime - actualTime;
    return new Date(tempDate.getTime() + offset);
}
export function getSimpleDateTimeFromJSDate(date, timeZone) {
    const d = convertJSDateToTimeZone(date, timeZone);
    return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        date: d.getDate(),
        hour: d.getHours(),
        minute: d.getMinutes(),
        _weekDay: d.getDay()
    };
}
export function getSimpleDateFromJSDate(date, timeZone) {
    const d = convertJSDateToTimeZone(date, timeZone);
    return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        date: d.getDate(),
        _weekDay: d.getDay()
    };
}
export function getSimpleDateTimeFromIso8602(dateString, timeZone) {
    const date = new Date(dateString);
    if (isNaN(date.getTime()))
        throw new Error(`Invalid ISO 8601 date string: ${dateString.slice(0, 30).replace(/[^a-zA-Z0-9,./ -]+/g, '')}`);
    return getSimpleDateTimeFromJSDate(date, timeZone);
}
export function getSimpleDateFromIso8602(dateString, timeZone) {
    const date = new Date(dateString);
    if (isNaN(date.getTime()))
        throw new Error(`Invalid ISO 8601 date string: ${dateString.slice(0, 30).replace(/[^a-zA-Z0-9,./ -]+/g, '')}`);
    return getSimpleDateFromJSDate(date, timeZone);
}
export function getYMDHMFromJSDate(date, timeZone) {
    return formatYMDHM(getSimpleDateTimeFromJSDate(date, timeZone));
}
export function getJSDateFromYMDHM(dateString, timeZone) {
    const sd = parseYMDHM(dateString);
    if (!sd)
        throw new Error(`Invalid date string: ${dateString.slice(0, 30).replace(/[^a-zA-Z0-9,./ -]+/g, '')}. Expected format is YYYYMMDDHHmm`);
    return getJSDateFromSimpleDate(sd, timeZone);
}
export function getOptionalJSDateFromYMDHM(date, timeZone) {
    if (date == undefined)
        return undefined;
    return getJSDateFromYMDHM(date, timeZone);
}
export function getNullableJSDateFromYMDHM(date, timeZone) {
    if (date == null)
        return date;
    if (!date)
        return null; // empty string is null
    return getJSDateFromYMDHM(date, timeZone);
}
export function getEpochMsFromSimpleDateTime(dateTime, timeZone) {
    return getJSDateFromSimpleDate(dateTime, timeZone).getTime();
}
